import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { ReactNode, SyntheticEvent, useCallback } from 'react';

import { useFirebase } from '@hooks/useFirebase';
import {
  FontSizes,
  Radius,
  Sizes,
  Spacing,
  Typography,
} from 'chatie-design-system';
import { Icon, Tap } from 'chatie-design-system/icon';

import SvgIcon from '@components/common/SvgIcon';

import IcLock from '@public/icons/ic_lock.svg';
import honorPartnershipStoryBadgeImg from '@public/images/partnership/honor/story_badge.png';

import { LIBRARY_SIZE } from '@pages/library';

import { StorySaleType, StoryTheme } from '@constants';
import { ISimpleUser, IStory } from '@model';
import { media } from '@styles/css';
import { differenceInDays } from '@utils/date';
import { OnClickProps, simplifyNumber } from '@utils/misc';

const HIDDEN_STATS_STORY_IDS = [9817116, 9817120, 9817119, 9817121];

import Avatar, { AvatarSize } from '../common/Avatar';
import Badge, { BadgeProps, useBadgePresets } from '../common/Badge';
import EFinderImage, { OptimizeEFinderImage } from '../common/EFinderImage';
import Image from '../common/Image';
import Nickname from '../user/Nickname';

export interface StoryProps
  extends Pick<
    IStory,
    | 'id'
    | 'title'
    | 'coverImage'
    | 'summary'
    | 'theme'
    | 'saleType'
    | 'type'
    | 'hasHonorPartnership'
    | 'hasNewEpisode'
    | 'firstEpisodePublished'
  > {
  tapCount?: number;
  user?: ISimpleUser;
  ad?: boolean;
  rank?: number | ReactNode;
  label?: ReactNode;
  isLibrary?: boolean;
  isInflow?: boolean;
  isActive?: boolean;
  isMyPage?: boolean;
}

const tagCSS = css`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`;

const customTagCSS =
  (type: 'tap_novel' | 'waitfree' | 'original') => (theme: Theme) =>
    css`
      padding: 2px 3px;
      color: ${theme.true.white};
      font-size: 10px;
      background-color: ${type === 'tap_novel'
        ? theme.service.violet
        : type === 'waitfree'
        ? theme.colors.yellow500
        : theme.colors.red500};
      border-radius: 3px;
    `;

export const StoryTag = ({
  theme,
  saleType,
  type,
  hasHonorPartnership,
}: Pick<IStory, 'theme' | 'saleType' | 'type' | 'hasHonorPartnership'>) => {
  const { t } = useTranslation();

  return hasHonorPartnership ? (
    <div css={tagCSS}>
      <Image
        src={honorPartnershipStoryBadgeImg}
        width={55}
        height={16}
        alt={t('명예 작품')}
      />
    </div>
  ) : theme === StoryTheme.tapNovel ? (
    <div css={[tagCSS, customTagCSS('tap_novel')]}>{t('탭소설')}</div>
  ) : saleType === StorySaleType.standardWaitfree ? (
    <div css={[tagCSS, customTagCSS('waitfree')]}>{t('기다리면무료')}</div>
  ) : type === 'original' ? (
    <div css={[tagCSS, customTagCSS('original')]}>{t('오리지널')}</div>
  ) : null;
};

/**
 * Component
 */

const storyCSS = css`
  position: relative;
  display: flex;
  align-items: center;
  padding: ${Spacing.small}px ${Spacing.medium}px;
  cursor: pointer;
`;

const srStoryCSS = css`
  position: relative;
  display: flex;
  align-items: center;
  padding: ${Spacing.small}px ${Spacing.medium}px 0px;
  cursor: pointer;
  min-width: 0;
`;
const rsStoryCSS = (theme: Theme) => css`
  position: relative;
  display: flex;
  align-items: center;
  max-width: calc(100% - 36px);
  border-radius: 3px;
  border: 1px solid ${theme.border.lightest};
`;

const CoverImageSize = {
  [Sizes.small]: css`
    width: 72px;
    height: 54px;
    border-radius: ${Radius.xsmall}px;
  `,
  [Sizes.medium]: css`
    width: 98px;
    height: 72px;
    border-radius: ${Radius.small}px;
  `,
  ['library']: css`
    width: 98px;
    height: 72px;
    border-radius: ${Radius.small}px;
  `,
};

export const coverImageCSS =
  (size: Sizes.small | Sizes.medium | typeof LIBRARY_SIZE) => (theme: Theme) =>
    css`
      position: relative;
      width: 98px;
      height: 72px;
      ${CoverImageSize[size]}
      border: 1px solid ${theme.border.lightest};
      overflow: hidden;
      flex-shrink: 0;

      .inactive {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: absolute;
        width: 98px;
        height: 72px;
        ${CoverImageSize[size]}
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1;
        overflow: hidden;
        color: ${theme.var.white};
        ${Typography.body11};
      }
    `;

const infoCSS = (theme: Theme) => css`
  position: relative;
  height: 72px;
  margin-left: ${Spacing.xsmall}px;
  overflow: hidden;
  flex-grow: 1;

  h6 {
    span + span {
      margin-left: ${Spacing.xxsmall}px;
    }
  }

  p {
    margin-top: ${Spacing.xhsmall}px;
    font-size: ${FontSizes.xsmall}px;
    color: ${theme.var.gray600};
  }
`;

const srInfoCSS = (theme: Theme) => css`
  position: relative;
  height: 56px;
  overflow: hidden;
  flex-grow: 1;
  min-width: 0;

  ${media.desktop} {
    height: auto;
    min-height: 72px;
  }

  h6 {
    color: ${theme.var.black};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .description {
    margin-top: 2px;
    ${Typography.body12};
    color: ${theme.var.gray600};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${media.desktop} {
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
  .info {
    ${Typography.body12};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }
  .tapCount {
    color: ${theme.colors.main500};
    font-weight: 600;
    line-height: normal;
  }
  .nickname {
    margin-left: ${Spacing.xsmall}px;
    color: ${theme.var.gray400};
    font-weight: 400;
  }
`;

const rsInfoCSS = (theme: Theme) => css`
  position: relative;
  padding: 11px ${Spacing.small}px;
  width: calc(100vw - 118px);
  flex-grow: 1;
  height: 90px;

  .title {
    color: ${theme.colors.main500};
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    display: flex;
    align-items: center;
    width: calc(100vw - 118px - 52px);
  }

  .nickname {
    display: flex;
    align-items: center;
    font-size: 11px;
    margin-top: 4px !important;
    width: calc(100vw - 118px - 64px);
    overflow: hidden; /* 추가 */
    text-overflow: ellipsis; /* 추가 */
    white-space: nowrap; /* 줄 바꿈 방지 */
  }
  .label {
    margin-right: 3px;
    height: 15px;
    span {
      display: block !important;
      width: 21px;
      height: 15px;
      font-size: 8px;
      text-align: center;
    }
  }

  h6 {
    display: flex;
    vertical-align: middle;
    height: 15px;
    align-text: center;
    text-align: center;
    align-items: center;
    width: calc(100vw - 118px - 52px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    margin-top: 4px;
    font-size: ${FontSizes.xsmall}px;
    color: ${theme.var.gray400};
    font-family: 'Apple SD Gothic Neo';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
  }

  .description {
    margin-top: 4px;
    font-size: 11px;
    width: calc(100vw - 118px - 52px);
    color: ${theme.var.gray400};
    font-family: 'Apple SD Gothic Neo';
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    max-height: 32px;
    min-height: 32px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-all;
  }
`;

const userCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  position: absolute;
  left: 0;
  bottom: 4px;
  font-size: ${FontSizes.xsmall}px;
  color: ${theme.var.gray500};

  > span {
    margin-left: ${Spacing.xxsmall}px;
  }
`;

const tapCSS = (theme: Theme) => css`
  position: absolute;
  right: 0;
  bottom: 2px;
  font-size: ${FontSizes.xsmall}px;
  color: ${theme.var.gray500};
`;

const rankCSS = (theme: Theme) => css`
  width: 28px;
  flex-shrink: 0;
  margin-left: ${Spacing.xsmall}px;
  text-align: center;
  color: ${theme.var.black};
`;

const srRankCSS = (theme: Theme) => css`
  width: 32px;
  flex-shrink: 0;
  text-align: center;
  color: ${theme.var.black};
  display: flex;
  algin-items: center;
  justify-content: center;

  .label {
    span {
      display: block !important;
      width: 23px;
      height: 16px;
      font-size: 8px;
      text-align: center;
    }
  }
`;

const Story = ({
  rank,
  label,
  onClick,
  isLibrary,
  isMyPage,
  isInflow = true,
  isActive = true,
  ...story
}: StoryProps & OnClickProps<'onClick'>) => {
  const {
    id,
    title,
    coverImage,
    summary,
    user,
    tapCount,
    hasNewEpisode,
    firstEpisodePublished,
  } = story;

  const { t } = useTranslation();

  const theme = useTheme();

  const { inflow } = useFirebase();

  const { yellow, red } = useBadgePresets();

  const handleClick = (e: SyntheticEvent<HTMLElement>) => {
    if (isInflow) {
      inflow(id);
    }
    onClick?.(e);
  };

  const renderLabels = useCallback(() => {
    return (
      <>
        {hasNewEpisode && <Badge text="UP" {...yellow} />}
        {firstEpisodePublished &&
          differenceInDays(new Date(), firstEpisodePublished) < 2 && (
            <Badge text="NEW" {...red} />
          )}
      </>
    );
  }, [hasNewEpisode, firstEpisodePublished]);

  return (
    <article css={storyCSS} data-id={id} onClick={handleClick}>
      <div css={coverImageCSS(isLibrary ? LIBRARY_SIZE : Sizes.medium)}>
        {isMyPage && !isActive && (
          <div className="inactive">
            <SvgIcon icon={IcLock} d={24} />
            비공개
          </div>
        )}
        <StoryTag {...story} />
        <EFinderImage image={coverImage} h={96} />
      </div>
      {rank && (
        <div css={rankCSS}>
          <h6>{rank}</h6>
        </div>
      )}
      <div css={infoCSS}>
        <h6 className="ellipsis align-middle">
          {renderLabels()}
          {label}
          <span>{title}</span>
        </h6>
        <p className="ellipsis">{summary}</p>

        {user ? (
          <div css={userCSS}>
            <Avatar size={AvatarSize.xsmall} {...user} />
            <Nickname {...user} />
          </div>
        ) : null}
        {tapCount !== undefined && !HIDDEN_STATS_STORY_IDS.includes(id) && (
          <div css={tapCSS} className="icon-text">
            <Icon icon={Tap} color={theme.var.gray500} size={Sizes.small} />
            <span>{t('simplifyNumber', { val: tapCount ?? 0 })}</span>
          </div>
        )}
      </div>
    </article>
  );
};

export default Story;

/**
 * Other view types
 */
export const storyWrapCSS = css`
  position: relative;
`;

export const storyActionCSS = css`
  top: 0;
  right: 0;
  padding-top: ${Spacing.small}px;
  padding-right: ${Spacing.small}px;
`;

const storyCardCSS = css`
  display: inline-block;
  width: 72px;

  p {
    margin-top: ${Spacing.xxsmall}px;
    font-size: ${FontSizes.xsmall}px;
  }
`;

export const StoryCard = ({
  onClick,
  isInflow = true,
  ...story
}: StoryProps & OnClickProps<'onClick'>) => {
  const { id, coverImage, title } = story;
  const { inflow } = useFirebase();

  const handleClick = (e: SyntheticEvent<HTMLElement>) => {
    if (isInflow) {
      inflow(id);
    }
    onClick?.(e);
  };

  return (
    <article css={storyCardCSS} onClick={handleClick}>
      <div css={coverImageCSS(Sizes.small)}>
        <StoryTag {...story} />
        <EFinderImage image={coverImage} h={54} />
      </div>
      <p className="ellipsis">{title}</p>
    </article>
  );
};

const rrTitleCSS = css`
  position: absolute;
  bottom: 5px;
  padding: 0px 8px;
  width: 100%;
  text-overflow: hidden;
  z-index: 9;
  color: white;
`;

const rrRankCSS = (theme: Theme) => css`
  position: absolute;
  top: 0;
  width: 20px;
  height: 20px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.main500};
  z-index: 9;
  color: white;
`;

const rrStoryCardCSS = css`
  width: 100%;
  height: 100%;
  p {
    font-size: ${FontSizes.xsmall}px;
  }

  .badge-container {
    z-index: 2;
  }
`;

const rrCoverImageCSS = (theme: Theme) =>
  css`
    position: relative;
    width: 100%;
    height: 100%;
    border: 1px solid ${theme.border.lightest};
    border-radius: 3px;

    overflow: hidden;
    flex-shrink: 0;
  `;

const rsCoverImageCSS = (theme: Theme) =>
  css`
    position: relative;
    width: 120px;
    height: 90px;
    border: 1px solid ${theme.border.lightest};
    border-radius: 3px 0px 0px 3px;

    overflow: hidden;
    flex-shrink: 0;
  `;

const srCoverImageCSS = (theme: Theme) =>
  css`
    position: relative;
    width: 75px;
    height: 56px;
    border: 1px solid ${theme.border.lightest};
    border-radius: 3px;

    overflow: hidden;
    flex-shrink: 0;

    ${media.desktop} {
      width: 98px;
      height: 72px;
    }
  `;
const overalyCSS = css`
  position: absolute;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
  z-index: 10;
  opacity: 0.3;
  background-color: black;
`;
const moreCSS = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 11;
  color: white;

  p {
    margin-top: 3px;
  }
`;

export const RRStoryCard = ({
  onClick,
  isInflow = true,
  rank,
  overlay,
  isAd,
  badgePreset,
  ...story
}: StoryProps &
  OnClickProps<'onClick'> & {
    overlay: boolean;
    width?: number | string;
    isAd?: boolean;
    badgePreset?: Pick<BadgeProps, 'color' | 'backgroundColor' | 'border'>;
  }) => {
  const { id, coverImage, title } = story;
  const { inflow } = useFirebase();
  const { t } = useTranslation();

  const handleClick = (e: SyntheticEvent<HTMLElement>) => {
    if (isInflow) {
      inflow(id);
    }
    onClick?.(e);
  };

  return (
    <article css={rrStoryCardCSS} onClick={handleClick}>
      <div css={rrCoverImageCSS}>
        {rank && <p css={rrRankCSS}>{rank}</p>}
        {isAd && (
          <div className="badge-container">
            <Badge text={t('홍보')} {...badgePreset} className={'rank'} />
          </div>
        )}
        <OptimizeEFinderImage image={coverImage} h={54} layout={'fill'} />
        {overlay && (
          <div css={moreCSS}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="16" cy="16" r="15.5" stroke="white" />
              <path
                d="M16.5 10V22"
                stroke="white"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22.5 16L10.5 16"
                stroke="white"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <p>{t('더보기')}</p>
            <div css={overalyCSS}></div>
          </div>
        )}
        {!isAd && <StoryTag {...story} />}
        {!overlay && (
          <p className="ellipsis" css={rrTitleCSS}>
            {title}
          </p>
        )}
      </div>
    </article>
  );
};

export const RSStoryCard = ({
  label,
  onClick,
  isInflow = true,
  ...story
}: StoryProps & OnClickProps<'onClick'>) => {
  const { id, title, coverImage, summary, user } = story;
  const ellipsisedTitle =
    title?.length > 22 ? title.slice(0, 22) + '...' : title;

  const { inflow } = useFirebase();

  const handleClick = (e: SyntheticEvent<HTMLElement>) => {
    if (isInflow) {
      inflow(id);
    }
    onClick?.(e);
  };

  return (
    <article css={rsStoryCSS} data-id={id} onClick={handleClick}>
      <div css={rsCoverImageCSS}>
        <StoryTag {...story} />
        <OptimizeEFinderImage image={coverImage} h={96} />
      </div>
      <div css={rsInfoCSS}>
        <h6 className="ellipsis align-middle">
          {!!label && <span className="label">{label}</span>}
          <span className="title">{ellipsisedTitle}</span>
        </h6>
        <p className="description ">{summary}</p>
        {!!user && <p className="ellipsis nickname">{'by ' + user.nickname}</p>}
      </div>
    </article>
  );
};

export const SRStoryCard = ({
  rank,
  label,
  onClick,
  isInflow = true,
  ...story
}: StoryProps & OnClickProps<'onClick'>) => {
  const { id, title, coverImage, summary, user, tapCount } = story;
  const tapNumber = simplifyNumber({ value: tapCount ?? 0 });

  const { t } = useTranslation();

  const { inflow } = useFirebase();
  const handleClick = (e: SyntheticEvent<HTMLElement>) => {
    if (isInflow) {
      inflow(id);
    }
    onClick?.(e);
  };

  return (
    <article
      css={srStoryCSS}
      data-id={id}
      onClick={handleClick}
      suppressHydrationWarning={true}
    >
      <div css={srCoverImageCSS}>
        <StoryTag {...story} />
        <OptimizeEFinderImage image={coverImage} h={96} />
      </div>
      {rank && (
        <div css={srRankCSS}>
          <h6>{rank}</h6>
        </div>
      )}
      {label && (
        <div css={srRankCSS}>
          <div className="label">{label}</div>
        </div>
      )}
      <div css={srInfoCSS}>
        <h6 className="ellipsis align-middle title">
          <span>{title}</span>
        </h6>
        <p className="ellipsis description">{summary}</p>
        <p className="info">
          {!!tapCount && !HIDDEN_STATS_STORY_IDS.includes(id) && <span className="tapCount">{t(tapNumber)}</span>}
          {!!user && (
            <span className="ellipsis nickname">{'by ' + user.nickname}</span>
          )}
        </p>
      </div>
    </article>
  );
};
